/** Who gets recorded: the sampling fraction and the user/IP allow-deny lists. */
const en = {
  title: 'Who gets recorded',
  subtitle:
    'Record a sample of your traffic instead of every visit, and keep specific people or networks in or out.',
  project: 'Project',
  noProject: 'Create a project first.',
  saved: 'Saved.',

  sampling: {
    heading: 'Sampling',
    intro:
      'At 100% every session is recorded. Lowering it reduces storage and the data leaving your site, in that proportion.',
    rate: 'Record this share of sessions',
    mode: 'Draw the sample per',
    modeUser: 'Visitor',
    modeSession: 'Session',
    modeUserHelp:
      'A visitor is either always recorded or never recorded. Their whole journey is kept, which is what funnels and path analysis need.',
    modeSessionHelp:
      'Each visit is decided on its own. A fair cross-section of traffic, but any one person’s sessions arrive only partly.',
    sdkNote:
      'Applied in the browser, so it also saves bandwidth. Sites loading the SDK from this deployment get it automatically; sites on the npm package need @replayops/sdk 0.1.2 or newer, and an older package ignores this setting.',
  },

  rules: {
    userHeading: 'Specific people',
    ipHeading: 'Specific networks',
    userIntro:
      'Matched against the user id you pass to identify() and the email in its traits, case-insensitively.',
    ipIntro: 'A single address, or a CIDR block such as 10.0.0.0/8. IPv4 and IPv6 both work.',
    mode: 'Mode',
    modeOff: 'Off, the list is ignored',
    modeAllow: 'Record only those listed',
    modeDeny: 'Record everyone except those listed',
    addUser: 'User id or email',
    addIp: 'Address or CIDR block',
    note: 'Why (optional)',
    add: 'Add',
    remove: 'Remove',
    empty: 'Nothing listed yet.',
    emptyAllowWarning:
      'The list is empty while “record only those listed” is on, so nothing is being recorded for this project.',
    allowAnonWarning:
      'While “record only those listed” is on, visitors who have not been identified yet are not recorded, including everything before sign-in.',
    count: (n: number) => `${n} ${n === 1 ? 'entry' : 'entries'}`,
  },

  enforcement: {
    heading: 'How this is applied',
    body:
      'Rules are enforced when events arrive, not only in the browser, so a modified page cannot opt back in. The browser is told the user list as hashes rather than plain values, so the page cannot read who is on it, and stops recording as soon as it recognises its own identity there. A rule often becomes applicable only mid-session, because identify() usually runs after the first frames are recorded. What was already stored for that session is then deleted.',
  },
};

const tr: typeof en = {
  title: 'Kimler kaydedilsin',
  subtitle:
    'Her ziyareti değil, trafiğinizin bir örneklemini kaydedin; belirli kişileri veya ağları kapsam dışında ya da yalnızca onları kapsamda tutun.',
  project: 'Proje',
  noProject: 'Önce bir proje oluşturun.',
  saved: 'Kaydedildi.',

  sampling: {
    heading: 'Örnekleme',
    intro:
      '%100’de her oturum kaydedilir. Oranı düşürdükçe depolama ve sitenizden çıkan veri aynı oranda azalır.',
    rate: 'Oturumların bu kadarını kaydet',
    mode: 'Örneklemi neye göre seç',
    modeUser: 'Ziyaretçi',
    modeSession: 'Oturum',
    modeUserHelp:
      'Bir ziyaretçi ya hep kaydedilir ya hiç kaydedilmez. Yolculuğunun tamamı elde kalır; huni ve yol analizinin ihtiyacı budur.',
    modeSessionHelp:
      'Her ziyaret kendi başına karara bağlanır. Trafiğin adil bir kesitini verir, ama tek bir kişinin oturumları parçalı gelir.',
    sdkNote:
      'Karar tarayıcıda verildiği için bant genişliğinden de tasarruf eder. SDK’yı bu kurulumdan yükleyen siteler bunu kendiliğinden alır; npm paketini kullananlar için @replayops/sdk 0.1.2 veya üstü gerekir, eski pakette bu ayar dikkate alınmaz.',
  },

  rules: {
    userHeading: 'Belirli kişiler',
    ipHeading: 'Belirli ağlar',
    userIntro:
      'identify() ile verdiğiniz kullanıcı kimliği ve trait’lerdeki e-posta ile karşılaştırılır; büyük/küçük harf farkı gözetilmez.',
    ipIntro: 'Tek bir adres ya da 10.0.0.0/8 gibi bir CIDR bloğu. IPv4 ve IPv6 çalışır.',
    mode: 'Mod',
    modeOff: 'Kapalı, liste dikkate alınmaz',
    modeAllow: 'Yalnızca listedekileri kaydet',
    modeDeny: 'Listedekiler hariç herkesi kaydet',
    addUser: 'Kullanıcı kimliği veya e-posta',
    addIp: 'Adres veya CIDR bloğu',
    note: 'Gerekçe (isteğe bağlı)',
    add: 'Ekle',
    remove: 'Kaldır',
    empty: 'Henüz kimse yok.',
    emptyAllowWarning:
      '“Yalnızca listedekileri kaydet” açık ama liste boş. Bu projede şu an hiçbir şey kaydedilmiyor.',
    allowAnonWarning:
      '“Yalnızca listedekileri kaydet” açıkken henüz tanımlanmamış ziyaretçiler kaydedilmez. Girişten önceki her şey dahil.',
    count: (n: number) => `${n} kayıt`,
  },

  enforcement: {
    heading: 'Bu kural nasıl uygulanıyor',
    body:
      'Kurallar yalnızca tarayıcıda değil, olaylar sunucuya ulaştığında da uygulanır; böylece değiştirilmiş bir sayfa kendini yeniden kapsama alamaz. Tarayıcıya kullanıcı listesi düz değerler değil hash’ler olarak gönderilir. Sayfa listede kimin olduğunu okuyamaz, yalnızca kendi kimliğini tanıdığı anda kaydı durdurur. Kural ancak oturum ortasında uygulanabilir hale gelirse (identify() genelde ilk karelerden sonra çalışır) o oturum için saklanmış olan veri silinir.',
  },
};

export const recording = { en, tr };
